import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2Icon, MailCheckIcon, SaveIcon, ChevronLeftIcon } from 'lucide-react';
import { toast } from 'sonner';
import { 
  createUserAsAdmin, 
  updateUserAsAdmin, 
  getBrowserUrl, 
  sendActivationEmailAsAdmin,
  listUsers,
  listRegions,
  type RegionRecord
} from '@/lib/auth-client';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

export function UserFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!(id && id !== 'new');
  
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [regions, setRegions] = useState<RegionRecord[]>([]);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'user' as 'admin' | 'user',
    status: 'Actif' as 'Actif' | 'Inactif',
    regionId: '',
  });

  useEffect(() => {
    async function init() {
      // Fetch regions always
      const regionsRes = await listRegions();
      if (regionsRes.data?.data) {
        setRegions(regionsRes.data.data);
      }

      if (isEdit) {
        // Fetch specific user
        const usersRes = await listUsers(); 
        const found = usersRes.data?.items.find(u => u.id === id);
        
        if (found) {
          setFormData({
            name: found.name,
            email: found.email,
            role: found.role === 'admin' ? 'admin' : 'user',
            status: found.status === 'Inactif' ? 'Inactif' : 'Actif',
            regionId: found.regions?.[0]?.id || '',
          });
        } else {
          toast.error("Membre introuvable");
          navigate('/admin/users');
        }
        setIsLoading(false);
      }
    }
    void init();
  }, [id, isEdit, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (formData.role === 'user' && !formData.regionId) {
      toast.error('Une région doit être assignée au commercial.');
      setIsSubmitting(false);
      return;
    }

    try {
      if (isEdit && id) {
        const res = await updateUserAsAdmin(id, {
          name: formData.name,
          role: formData.role,
          status: formData.status,
          regionId: formData.role === 'user' ? formData.regionId || null : null,
        });
        if (res.data?.success) {
          toast.success("Membre mis à jour");
          navigate('/admin/users');
        } else {
          toast.error("Erreur lors de la mise à jour");
        }
      } else {
        // On génère un mot de passe aléatoire temporaire pour s'assurer que better-auth crée bien un record "Account"
        // Cela évitera les problèmes de session lors du set-password initial.
        const tempPassword = Math.random().toString(36).slice(-10) + Math.random().toString(36).slice(-10);
        
        const res = await createUserAsAdmin({
          email: formData.email,
          name: formData.name,
          role: formData.role,
          password: tempPassword,
          regionId: formData.role === 'user' ? formData.regionId : undefined,
        });

        if (res.error) {
          toast.error("Erreur lors de la création");
          setIsSubmitting(false);
          return;
        }

        await sendActivationEmailAsAdmin({
          callbackURL: getBrowserUrl(`/auth/set-password?email=${encodeURIComponent(formData.email)}`),
          email: formData.email,
        });
        
        toast.success("Membre créé et email envoyé");
        navigate('/admin/users');
      }
    } catch {
      toast.error("Une erreur est survenue");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-8 pb-20">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/admin/users')}
            className="-ml-2 mb-2 text-muted-foreground hover:text-foreground"
          >
            <ChevronLeftIcon className="size-4 mr-1" />
            Retour à la liste
          </Button>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            {isEdit ? "Modifier le membre" : "Ajouter un membre"}
          </h1>
          <p className="text-muted-foreground">
            {isEdit 
              ? `Modification du profil de ${formData.name || 'ce membre'}`
              : "Invitez un nouveau collaborateur à rejoindre la console de gestion."}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8 rounded-[var(--radius-surface-4xl)] border border-border bg-card p-8 shadow-[0_8px_24px_rgba(15,23,42,0.03)]">
        <FieldGroup>
          <div className="grid gap-8 md:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="user-name">Nom complet <span className="text-destructive">*</span></FieldLabel>
              <Input
                id="user-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Jean Dupont"
                className="h-12 bg-background border-border"
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="user-email">Email professionnel <span className="text-destructive">*</span></FieldLabel>
              <Input
                id="user-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled={isEdit}
                placeholder="jean@mutuale.fr"
                className="h-12 bg-background border-border disabled:opacity-50"
              />
            </Field>
          </div>

          <Field>
            <FieldLabel>Rôle & Permissions</FieldLabel>
            <RadioGroup
              value={formData.role}
              onValueChange={(val: 'admin' | 'user') => setFormData((current) => ({
                ...current,
                role: val,
                regionId: val === 'admin' ? '' : current.regionId,
              }))}
              className="grid gap-4 md:grid-cols-2"
            >
              {[
                { value: 'user', label: 'Commercial / Membre', desc: 'Accès restreint aux salons de ses régions affectées.' },
                { value: 'admin', label: 'Administrateur', desc: 'Accès complet aux statistiques, régions et membres.' }
              ].map((r) => (
                <label key={r.value} className={cn(
                  "flex flex-col gap-2 p-5 rounded-2xl border transition-all cursor-pointer relative text-left",
                  formData.role === r.value 
                    ? "border-black bg-black/[0.02] ring-1 ring-black dark:border-border dark:bg-muted dark:ring-border text-foreground" 
                    : "border-border hover:border-foreground/20 hover:bg-muted/30 text-foreground"
                )}>
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value={r.value} id={`role-${r.value}`} className="border-border" />
                    <span className="font-bold text-[15px]">{r.label}</span>
                  </div>
                  <span className="text-sm text-muted-foreground ml-7 leading-relaxed">{r.desc}</span>
                </label>
              ))}
            </RadioGroup>
          </Field>

          {isEdit && (
            <Field>
              <FieldLabel>Statut du compte</FieldLabel>
              <div className="inline-flex bg-muted p-1 rounded-2xl border border-border w-full md:w-auto">
                {(['Actif', 'Inactif'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setFormData({ ...formData, status: s })}
                    className={cn(
                      "px-6 py-2.5 text-sm font-semibold rounded-xl transition-all min-w-[100px]",
                      formData.status === s 
                        ? "bg-card shadow-sm text-foreground ring-1 ring-border" 
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </Field>
          )}

          <Field>
            <FieldLabel htmlFor="user-region">Région assignée</FieldLabel>
            <select
              id="user-region"
              value={formData.regionId}
              onChange={(event) => setFormData((current) => ({ ...current, regionId: event.target.value }))}
              disabled={formData.role === 'admin'}
              className="flex h-12 w-full items-center rounded-xl border border-border bg-background px-4 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">Sélectionnez une région...</option>
              {regions.filter((region) => region.status === 'Active').map((region) => (
                <option key={region.id} value={region.id}>
                  {region.name} ({region.code})
                </option>
              ))}
            </select>
            <p className="text-sm text-muted-foreground">
              Un commercial ne peut être rattaché qu'à une seule région active à la fois.
            </p>
          </Field>

          {!isEdit && (
            <div className="p-5 rounded-2xl border border-blue-100 bg-blue-50/30 dark:border-blue-500/10 dark:bg-blue-500/5">
              <div className="flex gap-4">
                <div className="flex size-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-500/10 shadow-sm shrink-0">
                  <MailCheckIcon className="size-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-blue-900 dark:text-blue-100 mb-1">Email d'activation</h4>
                  <p className="text-sm leading-relaxed text-blue-800/70 dark:text-blue-200/60">
                    Un lien sécurisé sera envoyé à l'adresse indiquée pour permettre la configuration du mot de passe et la première connexion.
                  </p>
                </div>
              </div>
            </div>
          )}
        </FieldGroup>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border mt-8">
          <Button 
            type="button" 
            variant="ghost" 
            onClick={() => navigate('/admin/users')} 
            disabled={isSubmitting}
            className="px-6"
          >
            Annuler
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting} 
            className="h-11 min-w-32 border-black bg-black text-white shadow-none transition-colors hover:border-black/85 hover:bg-black/85 hover:text-white dark:border-border dark:bg-muted dark:text-foreground dark:hover:border-foreground/20 dark:hover:bg-muted/80"
          >
            {isSubmitting ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : <SaveIcon className="mr-2 size-4" />}
            {isEdit ? "Enregistrer les modifications" : "Créer le compte Mutuale"}
          </Button>
        </div>
      </form>
    </div>
  );
}
